import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, styled, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { COLORS, ICONS, LinkUnstyled, unit } from '@hcr/ui'
import { isNotNull, isNotUndefined } from '@hcr/utils'

import { useHoliday } from '../../../../contexts'
import {
  useAccommodationDetailsQuery,
  useDestinationDetailsQuery,
  useIdToken,
  useRoomDetailsQuery,
} from '../../../../hooks'
import { LocaleNamespace, Path } from '../../../../models'
import { createLinkGoogleMaps, createRoomNameString, isOwnerUsage, to } from '../../../../utils'
import { WarningMissingData } from '../../warning-missing-data'
import {
  BookingDates,
  LayoutBookingOverview,
  LayoutBookingOverviewContent,
  LayoutBookingOverviewDivider,
  LayoutBookingOverviewHeader,
  LayoutBookingOverviewSection,
} from '../common'

import { BookingOverviewLoading } from './BookingOverviewLoading'

export const BookingOverview: FC = () => {
  const { t } = useTranslation()
  const idToken = useIdToken()
  const holiday = useHoliday()

  const accommodation = useAccommodationDetailsQuery(
    { idToken: String(idToken), accommodationId: Number(holiday.data.accommodationId) },
    { enabled: isNotNull(idToken) && isNotNull(holiday.data.accommodationId) }
  )

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(holiday.data.destinationId) },
    { enabled: isNotNull(holiday.data.destinationId) }
  )

  const roomDetails = useRoomDetailsQuery(
    { destinationId: String(holiday.data.destinationId), roomId: String(accommodation.data?.room_type_id) },
    { enabled: isNotNull(holiday.data.destinationId) && isNotUndefined(accommodation.data?.room_type_id) }
  )

  if (holiday.isSuccess && accommodation.isSuccess) {
    const destinationDetailsLink = destinationDetails.isSuccess
      ? createLinkGoogleMaps({
          label: destinationDetails.data.address,
          target: 'external',
          url: destinationDetails.data.address,
        })
      : null

    return (
      <>
        {(destinationDetails.isError || roomDetails.isError || accommodation.isError) && (
          <Box marginBottom={unit(1.5)}>
            <WarningMissingData />
          </Box>
        )}
        <LayoutBookingOverview>
          <Typography visibility='hidden' component='h1' sx={{ height: unit(0) }} fontWeight='initial'>
            {destinationDetails.data?.name}
          </Typography>

          <LayoutBookingOverviewHeader gap={unit(2)}>
            <Typography variant='bodyM'>{t('booking-overview.your-booking-at')}</Typography>

            <Box
              component='img'
              alt={destinationDetails.data?.name}
              src={destinationDetails.data?.logoUrl}
              width='auto'
              height={unit(9)}
              visibility={destinationDetails.isSuccess ? 'visible' : 'hidden'}
            />
          </LayoutBookingOverviewHeader>
          <LayoutBookingOverviewContent>
            <BookingDates
              checkInDate={accommodation.data.start_date}
              checkInHour={roomDetails.data?.checkInHour}
              checkOutDate={accommodation.data.end_date}
              checkOutHour={roomDetails.data?.checkOutHour}
            />
            <LayoutBookingOverviewDivider />
            <LayoutBookingOverviewSection label={t('booking-overview.accommodation')}>
              <Typography variant='bodyM'>
                {roomDetails.isSuccess ? createRoomNameString(roomDetails.data) : accommodation.data.room_type}
              </Typography>
              <Icons>
                <IconWithLabel>
                  <FontAwesomeIcon icon={ICONS.farMoonStars} />
                  <Typography component='span' variant='titleS'>
                    {accommodation.data.days}
                  </Typography>
                </IconWithLabel>
                <IconWithLabel>
                  <FontAwesomeIcon icon={ICONS.farFamilyPants} />
                  <Typography component='span' variant='titleS'>
                    {accommodation.data.adults + accommodation.data.children}
                  </Typography>
                </IconWithLabel>
              </Icons>
            </LayoutBookingOverviewSection>
            <LayoutBookingOverviewDivider />
            <LayoutBookingOverviewSection label={t('booking-overview.booking-status')}>
              <Typography variant='bodyM'>
                {t(accommodation.data.status, { ns: LocaleNamespace.AccommodationStatus })}
              </Typography>
            </LayoutBookingOverviewSection>
            <LayoutBookingOverviewSection label={t('booking-overview.payment-status')}>
              <Typography variant='bodyM'>
                {isOwnerUsage(accommodation.data)
                  ? t('booking-overview.owner-payment')
                  : t(accommodation.data.payment_status, { ns: LocaleNamespace.PaymentStatus })}
              </Typography>
            </LayoutBookingOverviewSection>
            {destinationDetails.isSuccess && destinationDetailsLink && (
              <LayoutBookingOverviewSection label={t('booking-overview.arrival-info')}>
                <Typography
                  component='a'
                  href={String(destinationDetailsLink.to)}
                  key={destinationDetailsLink.label}
                  target='_blank'
                  rel='external'
                  variant='textLinkM'
                  color={COLORS.linknega[500]}
                >
                  {destinationDetailsLink.label}
                  <FontAwesomeIcon style={{ marginLeft: unit(2) }} icon={ICONS.fasLocationArrow} />
                </Typography>
              </LayoutBookingOverviewSection>
            )}
            <Button component={LinkUnstyled} to={to(Path.BookingDetails)} variant='outlined' color='white'>
              {t('booking-overview.see-booking-details')}
            </Button>
          </LayoutBookingOverviewContent>
        </LayoutBookingOverview>
      </>
    )
  }

  return <BookingOverviewLoading />
}

const Icons = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${unit(5.5)};
  margin-top: ${unit(1)};
`

const IconWithLabel = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${unit(1.5)};
`
